"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Leaf, Download, FileText } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

export default function NativePlantingsPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem("councilSessionToken")
      
      if (!sessionToken) {
        router.push("/council/login")
        return
      }

      try {
        const response = await fetch("/api/council/verify", {
          headers: { "Authorization": `Bearer ${sessionToken}` },
        })

        if (!response.ok) {
          localStorage.removeItem("councilSessionToken")
          router.push("/council/login")
          return
        }

        setIsAuthenticated(true)
        
        // Track page access
        try {
          await fetch("/api/council/activity", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ 
              page: "/council/native-plantings",
              action: "council_native_plantings_view"
            }),
          })
        } catch (error) {
          console.error("Error tracking activity:", error)
        }
      } catch (error) {
        console.error("Auth verification error:", error)
        localStorage.removeItem("councilSessionToken")
        router.push("/council/login")
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [router])

  const handleDownloadLandscaping = async () => {
    const sessionToken = localStorage.getItem("councilSessionToken")
    
    if (!sessionToken) {
      alert("Session expired. Please log in again.")
      router.push("/council/login")
      return
    }
    
    try {
      const response = await fetch("/api/council/landscaping", {
        headers: { 
          "Authorization": `Bearer ${sessionToken}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to download document`)
      }

      const blob = await response.blob()
      
      // Check if we got a PDF blob
      if (blob.type !== "application/pdf" && blob.size === 0) {
        throw new Error("Received empty or invalid file")
      }
      
      // Ensure we're in browser environment
      if (typeof window === "undefined" || typeof document === "undefined") {
        throw new Error("Download is only available in browser environment")
      }
      
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = "landscaping.pdf"
      anchor.style.display = "none"
      document.body.appendChild(anchor)
      anchor.click()
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(anchor)
        window.URL.revokeObjectURL(url)
      }, 100)

      // Track download
      try {
        await fetch("/api/council/activity", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({ 
            page: "/council/native-plantings",
            action: "council_landscaping_download",
          }),
        })
      } catch (error) {
        console.error("Error tracking download:", error)
      }
    } catch (error) {
      console.error("Download error:", error)
      const errorMessage = error instanceof Error ? error.message : "Failed to download document. Please try again."
      alert(errorMessage)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b0861] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push("/council/dashboard")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#5b0861]">PetAftercare</h1>
                <p className="text-sm text-gray-600">Indigenous Planting</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Introduction */}
        <Card className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#5b0861]">
              <Leaf className="h-6 w-6" />
              Indigenous Planting Commitment
            </CardTitle>
            <CardDescription>
              Indigenous planting is required as a mitigating tool to enhance the cultural landscape, increase indigenous habitat, filter sediment and sequester carbon
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              As part of our application for Resource Consent, we acknowledge and support the requirement for indigenous planting as a mitigation tool to enhance the cultural and ecological landscape. Our business, located in the foothills of Mount Grey / Maukatere, recognises the significant cultural and spiritual value of this maunga, and we are committed to ensuring that our site reflects these values through careful, respectful planning and native regeneration.
            </p>
          </CardContent>
        </Card>

        {/* Our Commitment */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Our Indigenous Planting Commitment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              We will implement an indigenous planting plan that supports:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Cultural Landscape Enhancement</h4>
                <p className="text-sm text-gray-700">
                  By prioritising species natural to the Canterbury Plains and foothills of Maukatere, restoring a connection to pre-European ecosystems and landscape identity.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-gray-900 mb-2">Habitat Restoration</h4>
                <p className="text-sm text-gray-700">
                  Through the planting of species that support native birds, invertebrates, and ecological resilience.
                </p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-semibold text-gray-900 mb-2">Water and Soil Protection</h4>
                <p className="text-sm text-gray-700">
                  By using plants that stabilise soil, and protect nearby waterways.
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                <h4 className="font-semibold text-gray-900 mb-2">Carbon Sequestration</h4>
                <p className="text-sm text-gray-700">
                  With planting densities and species that contribute to long-term carbon storage.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Species Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Species Selection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-700">
                Drawing from the DOC guide to Canterbury Plains native plants and local restoration projects, we propose the following species mix appropriate to our site's terrain, soil, and exposure:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Trees / Canopy Layer</h4>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• <strong>Plagianthus regius</strong> – Ribbonwood / Māhoe</li>
                    <li>• <strong>Pittosporum tenuifolium</strong> – Kōhūhū / Black Matipo</li>
                    <li>• <strong>Hoheria angustifolia</strong> – Narrow-leaved lacebark / Houhere</li>
                    <li>• <strong>Sophora microphylla</strong> – Kōwhai (for nectar-feeding birds)</li>
                  </ul>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Shrub Layer</h4>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• <strong>Olearia odorata</strong> – Tree daisy (threatened but suited to dry hill country)</li>
                    <li>• <strong>Coprosma propinqua</strong> – Mingimingi</li>
                    <li>• <strong>Melicytus alpinus</strong> – Porcupine shrub</li>
                  </ul>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Groundcover and Riparian</h4>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• <strong>Carex secta</strong> and <strong>Carex maorica</strong> – Tussock sedges for moisture-prone areas</li>
                    <li>• <strong>Leptinella dioica</strong> – Remuremu, a mat-forming species</li>
                    <li>• <strong>Muehlenbeckia complexa</strong> – Pohuehue, ground-binding and habitat-supporting</li>
                  </ul>
                </div>
              </div>
              <p className="text-sm text-gray-600 italic">
                This species list will be refined in consultation with a local native plant nursery and ecologist familiar with foothill restoration.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Planting Principles */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Planting Principles</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>We will prioritise eco-sourced plants from the Canterbury region where possible.</li>
              <li>Planting will take place in staggered groups to mimic natural bush edge succession.</li>
              <li>Mulching, fencing, and weed control will be implemented to ensure long-term survival.</li>
            </ul>
          </CardContent>
        </Card>

        {/* Engagement with Iwi */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Engagement with Iwi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <p className="text-gray-700 mb-3">
                We respectfully invite guidance from Ngāi Tūāhuriri on:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Species of cultural significance that we should prioritise or avoid.</li>
                <li>Tikanga or karakia we should observe when preparing or planting.</li>
                <li>Any opportunities to co-design or co-maintain part of this planting as a living connection to the whenua.</li>
              </ul>
              <p className="text-gray-700 font-medium">
                We are committed to this not just as a regulatory requirement, but as part of our long-term stewardship of the land and as an act of honouring mana whenua.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Landscaping PDF */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#5b0861]" />
              Landscaping Documentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Landscaping Plan</h4>
                <p className="text-sm text-gray-600">Detailed landscaping and planting documentation</p>
              </div>
              <Button
                onClick={handleDownloadLandscaping}
                variant="outline"
                className="flex-shrink-0"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
